import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { format } from "date-fns";
import type {
    PreTripInspectionItemResponseDto,
    PreTripInspectionResponseDto,
} from "../../../api/ptiApi";
import { getPtiAssetDisplayLabel, getPtiPositionLabel, PTI_POSITION_KEYS } from "../ptiConstants";
import {
    formatChecklistAnswer,
    getPtiChecklistQuestions,
    type PtiChecklistQuestion,
} from "../ptiChecklist";
import { isImageFile } from "../../../utils/fileUtils";

interface PtiDocumentPreviewProps {
    inspection: PreTripInspectionResponseDto;
    /** Optional id used when exporting the preview to PDF from the DOM. */
    exportRootId?: string;
}

const VEHICLE_BODY_ORDER = [
    PTI_POSITION_KEYS.DriverSide,
    PTI_POSITION_KEYS.Front,
    PTI_POSITION_KEYS.PassengerSide,
    PTI_POSITION_KEYS.Rear,
    PTI_POSITION_KEYS.Dashboard,
    PTI_POSITION_KEYS.Coolant,
    PTI_POSITION_KEYS.Oil,
    PTI_POSITION_KEYS.FifthWheel,
];

const VEHICLE_TIRE_ORDER = [
    PTI_POSITION_KEYS.SteerLeft,
    PTI_POSITION_KEYS.SteerRight,
    PTI_POSITION_KEYS.Drive1LeftOuter,
    PTI_POSITION_KEYS.Drive1LeftInner,
    PTI_POSITION_KEYS.Drive1RightInner,
    PTI_POSITION_KEYS.Drive1RightOuter,
    PTI_POSITION_KEYS.Drive2LeftOuter,
    PTI_POSITION_KEYS.Drive2LeftInner,
    PTI_POSITION_KEYS.Drive2RightInner,
    PTI_POSITION_KEYS.Drive2RightOuter,
];

const TRAILER_BODY_ORDER = [
    PTI_POSITION_KEYS.Front,
    PTI_POSITION_KEYS.DriverSide,
    PTI_POSITION_KEYS.PassengerSide,
    PTI_POSITION_KEYS.Rear,
    PTI_POSITION_KEYS.Doors,
    PTI_POSITION_KEYS.LeftInside,
    PTI_POSITION_KEYS.RightInside,
    PTI_POSITION_KEYS.InsideFloor,
];

const TRAILER_TIRE_ORDER = [
    PTI_POSITION_KEYS.Axle1LeftOuter,
    PTI_POSITION_KEYS.Axle1LeftInner,
    PTI_POSITION_KEYS.Axle1RightInner,
    PTI_POSITION_KEYS.Axle1RightOuter,
    PTI_POSITION_KEYS.Axle2LeftOuter,
    PTI_POSITION_KEYS.Axle2LeftInner,
    PTI_POSITION_KEYS.Axle2RightInner,
    PTI_POSITION_KEYS.Axle2RightOuter,
];

function hasImage(item: PreTripInspectionItemResponseDto): boolean {
    return Boolean(item.url) && (!item.contentType || isImageFile(item.contentType));
}

function orderByKeys(
    items: PreTripInspectionItemResponseDto[],
    order: string[]
): PreTripInspectionItemResponseDto[] {
    const rank = new Map(order.map((key, index) => [key, index]));
    return [...items].sort((a, b) => {
        const aRank = rank.get(a.positionKey) ?? 500;
        const bRank = rank.get(b.positionKey) ?? 500;
        if (aRank !== bRank) return aRank - bRank;
        return a.positionKey.localeCompare(b.positionKey);
    });
}

function chunkItems<T>(items: T[], size: number): T[][] {
    const rows: T[][] = [];
    for (let i = 0; i < items.length; i += size) {
        rows.push(items.slice(i, i + size));
    }
    return rows;
}

function formatCompactAnswer(question: PtiChecklistQuestion, answer: unknown): string {
    const label = formatChecklistAnswer(
        question,
        answer as Parameters<typeof formatChecklistAnswer>[1]
    );
    if (label === "Yes") return "Y";
    if (label === "No") return "N";
    if (label.startsWith("Yes —")) return "Y*";
    if (label.startsWith("No —")) return "N*";
    if (label === "Pass") return "PASS";
    if (label === "Fail") return "FAIL";
    return label;
}

const sectionTitleSx = {
    bgcolor: "#111",
    color: "#fff",
    px: 1,
    py: 0.5,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.4,
    textTransform: "uppercase" as const,
};

/** Fixed 4:3 frames — matches camera guide and Letter PDF photo slots. */
const bodyPhotoFrameSx = {
    width: "100%",
    aspectRatio: 4 / 3,
    flexShrink: 0,
    bgcolor: "#eee",
    border: "1px solid #bbb",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
};

const tirePhotoFrameSx = {
    width: "100%",
    aspectRatio: 4 / 3,
    flexShrink: 0,
    bgcolor: "#eee",
    border: "1px solid #bbb",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
};

/** Element box sized to the contained bitmap so html2canvas does not stretch. */
const photoImgSx = {
    maxWidth: "100%",
    maxHeight: "100%",
    width: "auto",
    height: "auto",
    objectFit: "contain" as const,
    objectPosition: "center",
    display: "block",
};

const PtiDocumentPreview: React.FC<PtiDocumentPreviewProps> = ({
    inspection,
    exportRootId,
}) => {
    const isVehicle = inspection.assetType === "Vehicle";
    const bodyItems = useMemo(() => {
        const items = inspection.items.filter((item) => item.itemType === "BodyPhoto");
        return orderByKeys(items, isVehicle ? VEHICLE_BODY_ORDER : TRAILER_BODY_ORDER);
    }, [inspection.items, isVehicle]);

    const tireItems = useMemo(() => {
        const items = inspection.items.filter((item) => item.itemType === "Tire");
        return orderByKeys(items, isVehicle ? VEHICLE_TIRE_ORDER : TRAILER_TIRE_ORDER);
    }, [inspection.items, isVehicle]);

    const checklistSections = useMemo(() => {
        const questions = getPtiChecklistQuestions(inspection.assetType).filter(
            (question) => inspection.checklistAnswers?.[question.key] !== undefined
        );
        const grouped = new Map<string, PtiChecklistQuestion[]>();
        questions.forEach((question) => {
            const section = (question.section?.trim() || "General").toUpperCase();
            const existing = grouped.get(section);
            if (existing) existing.push(question);
            else grouped.set(section, [question]);
        });
        return Array.from(grouped.entries());
    }, [inspection.assetType, inspection.checklistAnswers]);

    const bodyRows = useMemo(() => chunkItems(bodyItems, 3), [bodyItems]);
    const tireRows = useMemo(() => chunkItems(tireItems, 2), [tireItems]);
    const unitLabel = getPtiAssetDisplayLabel(inspection);

    return (
        <Box
            id={exportRootId}
            sx={{
                bgcolor: "#fff",
                color: "#111",
                p: { xs: 1.5, md: 2.5 },
                fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                // Matches Letter content aspect used by the PDF packer (~1080px tall at 816px wide).
                "--pti-pdf-page-height": "1080px",
                "--pti-pdf-page-width": "816px",
            }}
        >
            <Box sx={{ border: "2px solid #111", p: 1.5 }} data-pti-pdf-page-block>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 2,
                        alignItems: "flex-start",
                    }}
                >
                    <Box>
                        <Typography sx={{ color: "#c62828", fontWeight: 800, fontSize: 14 }}>
                            ADUT GLOBAL
                        </Typography>
                        <Typography sx={{ fontWeight: 800, fontSize: 20, lineHeight: 1.1 }}>
                            {isVehicle ? "TRUCK INSPECTION FORM" : "TRAILER INSPECTION FORM"}
                        </Typography>
                        <Typography sx={{ color: "#c62828", fontWeight: 700, fontSize: 12 }}>
                            {isVehicle ? "(TRACTOR)" : "(TRAILER)"}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            border: "1px solid #111",
                            minWidth: 140,
                            px: 1,
                            py: 0.75,
                        }}
                    >
                        <Typography sx={{ fontSize: 10, fontWeight: 700 }}>
                            {isVehicle ? "TRUCK #" : "TRAILER #"}
                        </Typography>
                        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{unitLabel}</Typography>
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr 1fr",
                            md: "repeat(4, 1fr)",
                        },
                        gap: 1.5,
                        mt: 1.5,
                        pt: 1.5,
                        borderTop: "1px solid #111",
                    }}
                >
                    {[
                        [
                            "DATE",
                            format(new Date(inspection.inspectionDate), "MMM d, yyyy h:mm a"),
                        ],
                        ["DRIVER", inspection.submittedByName || "—"],
                        ["STATUS", inspection.status],
                        [
                            "ODOMETER",
                            inspection.odometer != null
                                ? `${inspection.odometer.toLocaleString()} mi`
                                : "—",
                        ],
                    ].map(([label, value]) => (
                        <Box key={label}>
                            <Typography sx={{ fontSize: 10, fontWeight: 700 }}>{label}</Typography>
                            <Typography
                                sx={{
                                    fontSize: 13,
                                    borderBottom: "1px solid #111",
                                    pb: 0.25,
                                }}
                            >
                                {value}
                            </Typography>
                        </Box>
                    ))}
                </Box>
                <Typography sx={{ mt: 1, fontSize: 11, fontWeight: 700 }}>
                    TYPE OF INSPECTION: PRE-TRIP
                </Typography>
            </Box>

            {inspection.rejectionReason && (
                <Box
                    data-pti-pdf-page-block
                    sx={{
                        p: 1,
                        bgcolor: "#ffebee",
                        border: "1px solid #ef9a9a",
                        fontSize: 12,
                    }}
                >
                    Rejection reason: {inspection.rejectionReason}
                </Box>
            )}

            {bodyRows.map((row, rowIndex) => (
                <Box key={`body-row-${rowIndex}`} data-pti-pdf-page-block>
                    {rowIndex === 0 && (
                        <Box sx={{ ...sectionTitleSx, mb: 1 }}>Exterior / Body Photos</Box>
                    )}
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                            gap: 1.25,
                        }}
                    >
                        {row.map((item) => (
                            <Box
                                key={`${item.itemType}-${item.positionKey}`}
                                sx={{ border: "1px solid #111", p: 0.75 }}
                            >
                                <Typography sx={{ fontSize: 11, fontWeight: 700, mb: 0.5 }}>
                                    {getPtiPositionLabel(item.positionKey).toUpperCase()}
                                </Typography>
                                <Box sx={bodyPhotoFrameSx}>
                                    {hasImage(item) ? (
                                        <Box
                                            component="img"
                                            src={item.url!}
                                            alt={getPtiPositionLabel(item.positionKey)}
                                            data-pti-item-id={item.id}
                                            sx={photoImgSx}
                                        />
                                    ) : (
                                        <Typography sx={{ fontSize: 12, color: "#777" }}>
                                            No photo
                                        </Typography>
                                    )}
                                </Box>
                                {item.damageMarks?.map((mark) => (
                                    <Typography
                                        key={`${mark.zoneKey}-${mark.zoneNumber}`}
                                        sx={{
                                            fontSize: 11,
                                            mt: 0.5,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        #{mark.zoneNumber} {mark.zoneKey}
                                        {mark.note?.trim() ? `: ${mark.note.trim()}` : ""}
                                    </Typography>
                                ))}
                                {item.note?.trim() && (
                                    <Typography
                                        sx={{
                                            fontSize: 11,
                                            mt: 0.5,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {item.note.trim()}
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </Box>
                </Box>
            ))}

            {tireRows.map((row, rowIndex) => (
                <Box key={`tire-row-${rowIndex}`} data-pti-pdf-page-block>
                    {rowIndex === 0 && (
                        <Box sx={{ ...sectionTitleSx, mb: 1 }}>Tire Tread / Brake / PSI</Box>
                    )}
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                            gap: 1.25,
                        }}
                    >
                        {row.map((item) => (
                            <Box
                                key={`${item.itemType}-${item.positionKey}`}
                                sx={{
                                    border: "1px solid #111",
                                    p: 1,
                                    display: "grid",
                                    gridTemplateColumns: "minmax(140px, 42%) 1fr",
                                    gap: 1.25,
                                    alignItems: "center",
                                }}
                            >
                                <Box sx={tirePhotoFrameSx}>
                                    {hasImage(item) ? (
                                        <Box
                                            component="img"
                                            src={item.url!}
                                            alt={getPtiPositionLabel(item.positionKey)}
                                            data-pti-item-id={item.id}
                                            sx={photoImgSx}
                                        />
                                    ) : (
                                        <Typography sx={{ fontSize: 12, color: "#777" }}>
                                            No photo
                                        </Typography>
                                    )}
                                </Box>

                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "center",
                                        gap: 0.75,
                                        minWidth: 0,
                                    }}
                                >
                                    <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
                                        {getPtiPositionLabel(item.positionKey).toUpperCase()}
                                    </Typography>
                                    {[
                                        [
                                            "Tread",
                                            item.treadDepth != null
                                                ? `${item.treadDepth}/32`
                                                : "—",
                                        ],
                                        [
                                            "Brake",
                                            item.brakeMeasurement != null
                                                ? String(item.brakeMeasurement)
                                                : "—",
                                        ],
                                        [
                                            "PSI",
                                            item.tirePsi != null ? String(item.tirePsi) : "—",
                                        ],
                                    ].map(([label, value]) => (
                                        <Box
                                            key={label}
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                gap: 1,
                                                borderBottom: "1px solid #ddd",
                                                pb: 0.35,
                                                fontSize: 12,
                                            }}
                                        >
                                            <Typography
                                                component="span"
                                                sx={{ fontSize: 12, color: "#555" }}
                                            >
                                                {label}
                                            </Typography>
                                            <Typography
                                                component="span"
                                                sx={{ fontSize: 12, fontWeight: 700 }}
                                            >
                                                {value}
                                            </Typography>
                                        </Box>
                                    ))}
                                    {item.note?.trim() && (
                                        <Typography
                                            sx={{
                                                fontSize: 11,
                                                color: "#444",
                                                mt: 0.25,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {item.note.trim()}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>
            ))}

            {checklistSections.length > 0 && (
                <Box data-pti-pdf-page-block>
                    <Box sx={sectionTitleSx}>
                        {isVehicle ? "Vehicle Checklist" : "Trailer Checklist"}
                    </Box>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                            gap: 1,
                            mt: 1,
                        }}
                    >
                        {checklistSections.map(([section, questions]) => (
                            <Box key={section} sx={{ border: "1px solid #111" }}>
                                <Box sx={{ ...sectionTitleSx, fontSize: 10 }}>{section}</Box>
                                {questions.map((question) => (
                                    <Box
                                        key={question.key}
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            gap: 1,
                                            px: 1,
                                            py: 0.4,
                                            borderBottom: "1px solid #eee",
                                            fontSize: 11,
                                        }}
                                    >
                                        <span>{question.label}</span>
                                        <strong>
                                            {formatCompactAnswer(
                                                question,
                                                inspection.checklistAnswers?.[question.key]
                                            )}
                                        </strong>
                                    </Box>
                                ))}
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            <Box data-pti-pdf-page-block>
                <Box sx={sectionTitleSx}>Notes / Comments</Box>
                <Box sx={{ border: "1px solid #111", borderTop: 0, p: 1, minHeight: 48 }}>
                    {inspection.notes?.trim() ? (
                        <Typography sx={{ fontSize: 12, whiteSpace: "pre-wrap" }}>
                            {inspection.notes.trim()}
                        </Typography>
                    ) : (
                        <Typography sx={{ fontSize: 12, color: "#777", fontStyle: "italic" }}>
                            No notes recorded.
                        </Typography>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default PtiDocumentPreview;
